

// src/components/home/SalesCogsFilters/SalesCogsFilters.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { salesCogsFilterApi } from '../../../../api/home/salesCogsFilterApi';
import styles from './SalesCogsFilters.module.css';

const FilterDropdown = ({ label, options, selected, onSelect, onRemove, onSearch, searchable, loading }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearch(val);
        if (onSearch) onSearch(val);
    };

    const filtered = searchable
        ? options
        : options.filter(o => o.label?.toLowerCase().includes(search.toLowerCase()));

    const isSelected = (val) => selected.some(s => s.value === val);

    return (
        <div className={styles.filterItem} ref={ref}>
            <button
                className={`${styles.filterBtn} ${selected.length > 0 ? styles.filterBtnActive : ''}`}
                onClick={() => setOpen(!open)}
            >
                <span>{label}</span>
                {selected.length > 0 && (
                    <span className={styles.filterCount}>{selected.length}</span>
                )}
                <span className={styles.filterChevron}>{open ? '▲' : '▼'}</span>
            </button>

            {/* Selected tags */}
            {selected.length > 0 && (
                <div className={styles.selectedTags}>
                    {selected.map(s => (
                        <span key={s.value} className={styles.tag}>
                            {s.label}
                            <button onClick={() => onRemove(s.value)} className={styles.tagRemove}>✕</button>
                        </span>
                    ))}
                </div>
            )}

            {open && (
                <div className={styles.filterDropdown}>
                    <div className={styles.filterSearch}>
                        <input
                            type="text"
                            placeholder={`Search ${label}...`}
                            value={search}
                            onChange={handleSearchChange}
                            autoFocus
                            className={styles.filterSearchInput}
                        />
                    </div>
                    <div className={styles.filterOptions}>
                        {loading ? (
                            <div className={styles.filterLoading}>
                                <span className={styles.filterSpinner} /> Loading...
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className={styles.filterEmpty}>No results found</div>
                        ) : (
                            filtered.slice(0, 10).map(opt => (
                                <div
                                    key={opt.value}
                                    className={`${styles.filterOption} ${isSelected(opt.value) ? styles.filterOptionSelected : ''}`}
                                    onClick={() => {
                                        if (!isSelected(opt.value)) onSelect(opt);
                                        setOpen(false);
                                        setSearch('');
                                    }}
                                >
                                    <span className={styles.filterOptionCheck}>
                                        {isSelected(opt.value) ? '✓' : ''}
                                    </span>
                                    <span>{opt.label}</span>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const SalesCogsFilters = ({ userRoleId, onFiltersChange }) => {
    const [categories, setCategories]         = useState([]);
    const [customers, setCustomers]           = useState([]);
    const [salesEmployees, setSalesEmployees] = useState([]);
    const [contactPersons, setContactPersons] = useState([]);
    const [items, setItems]                   = useState([]);
    const [itemsLoading, setItemsLoading]     = useState(false);

    const [selectedCategories, setSelectedCategories]         = useState([]);
    const [selectedCustomers, setSelectedCustomers]           = useState([]);
    const [selectedSalesEmployees, setSelectedSalesEmployees] = useState([]);
    const [selectedContactPersons, setSelectedContactPersons] = useState([]);
    const [selectedItems, setSelectedItems]                   = useState([]);

    const showFilters      = userRoleId === 3 || userRoleId === 2;
    const showSalesEmployee = userRoleId === 3;

    // ─── Load filter options once ─────────────────────────────
    useEffect(() => {
        if (!showFilters) return;

        const loadFilters = async () => {
            try {
                const [cats, custs, cps] = await Promise.all([
                    salesCogsFilterApi.getCategories(),
                    salesCogsFilterApi.getCustomers(),
                    salesCogsFilterApi.getContactPersons(),
                ]);
                setCategories(cats);
                setCustomers(custs);
                setContactPersons(cps);

                if (showSalesEmployee) {
                    const sps = await salesCogsFilterApi.getSalesEmployees();
                    setSalesEmployees(sps);
                }

                const itms = await salesCogsFilterApi.getItems('');
                setItems(itms);
            } catch (err) {
                console.error('Error loading filters:', err);
            }
        };

        loadFilters();
    }, [showFilters, showSalesEmployee]);

    // ─── Notify parent on selection change (SINGLE useEffect) ─
    useEffect(() => {
        const filters = {};

        if (selectedCategories.length > 0)
            filters.category = selectedCategories.map(s => String(s.value)).join(',');
        if (selectedCustomers.length > 0)
            filters.customer = selectedCustomers.map(s => String(s.value)).join(',');
        if (selectedSalesEmployees.length > 0)
            filters.salesEmployee = selectedSalesEmployees.map(s => String(s.value)).join(',');
        if (selectedContactPersons.length > 0)
            filters.contactPerson = selectedContactPersons.map(s => String(s.value)).join(',');
        if (selectedItems.length > 0)
            filters.itemCode = selectedItems.map(s => String(s.value)).join(',');

        console.log('🔽 Filter change triggered:', filters);
        onFiltersChange(filters);

    }, [
        JSON.stringify(selectedCategories),
        JSON.stringify(selectedCustomers),
        JSON.stringify(selectedSalesEmployees),
        JSON.stringify(selectedContactPersons),
        JSON.stringify(selectedItems),
    ]);

    // ─── Debounced item search ────────────────────────────────
    const itemSearchTimer = useRef(null);
    const handleItemSearch = useCallback((search) => {
        clearTimeout(itemSearchTimer.current);
        setItemsLoading(true);
        itemSearchTimer.current = setTimeout(async () => {
            try {
                const data = await salesCogsFilterApi.getItems(search);
                setItems(data);
            } catch (err) {
                console.error('Item search error:', err);
            } finally {
                setItemsLoading(false);
            }
        }, 300);
    }, []);

    const hasAnyFilter =
        selectedCategories.length > 0 ||
        selectedCustomers.length > 0 ||
        selectedSalesEmployees.length > 0 ||
        selectedContactPersons.length > 0 ||
        selectedItems.length > 0;

    const clearAll = () => {
        setSelectedCategories([]);
        setSelectedCustomers([]);
        setSelectedSalesEmployees([]);
        setSelectedContactPersons([]);
        setSelectedItems([]);
    };

    if (!showFilters) return null;

    return (
        <div className={styles.filtersWrapper}>
            <div className={styles.filtersRow}>
                <FilterDropdown
                    label="Customer"
                    options={customers}
                    selected={selectedCustomers}
                    onSelect={(opt) => setSelectedCustomers(prev => [...prev, opt])}
                    onRemove={(val) => setSelectedCustomers(prev => prev.filter(s => s.value !== val))}
                />
                <FilterDropdown
                    label="Category"
                    options={categories}
                    selected={selectedCategories}
                    onSelect={(opt) => setSelectedCategories(prev => [...prev, opt])}
                    onRemove={(val) => setSelectedCategories(prev => prev.filter(s => s.value !== val))}
                />
                {showSalesEmployee && (
                    <FilterDropdown
                        label="Sales Employee"
                        options={salesEmployees}
                        selected={selectedSalesEmployees}
                        onSelect={(opt) => setSelectedSalesEmployees(prev => [...prev, opt])}
                        onRemove={(val) => setSelectedSalesEmployees(prev => prev.filter(s => s.value !== val))}
                    />
                )}
                <FilterDropdown
                    label="Contact Person"
                    options={contactPersons}
                    selected={selectedContactPersons}
                    onSelect={(opt) => setSelectedContactPersons(prev => [...prev, opt])}
                    onRemove={(val) => setSelectedContactPersons(prev => prev.filter(s => s.value !== val))}
                />
                <FilterDropdown
                    label="Item Code"
                    options={items}
                    selected={selectedItems}
                    onSelect={(opt) => setSelectedItems(prev => [...prev, opt])}
                    onRemove={(val) => setSelectedItems(prev => prev.filter(s => s.value !== val))}
                    onSearch={handleItemSearch}
                    searchable
                    loading={itemsLoading}
                />

                {hasAnyFilter && (
                    <button className={styles.clearBtn} onClick={clearAll}>
                        ✕ Clear Filters
                    </button>
                )}
            </div>
        </div>
    );
};

export default SalesCogsFilters;